type CountedToolCallLabelInput = {
  label: string;
  count: number;
};

const IRREGULAR_SINGULARS: Record<string, string> = {
  entities: 'entity',
};

const IRREGULAR_PLURALS: Record<string, string> = {
  entity: 'entities',
};

function applyCase({
  source,
  target,
}: {
  source: string;
  target: string;
}): string {
  if (source.length === 0 || target.length === 0) {
    return target;
  }

  if (source === source.toUpperCase()) {
    return target.toUpperCase();
  }

  if (source[0] === source[0]?.toUpperCase()) {
    return `${target[0]?.toUpperCase() ?? ''}${target.slice(1)}`;
  }

  return target;
}

function toSingular(word: string): string {
  const lower = word.toLowerCase();
  const irregular = IRREGULAR_SINGULARS[lower];

  if (irregular) {
    return applyCase({ source: word, target: irregular });
  }

  if (lower.endsWith('s') && !lower.endsWith('ss')) {
    return word.slice(0, -1);
  }

  return word;
}

function toPlural(word: string): string {
  const lower = word.toLowerCase();
  const irregular = IRREGULAR_PLURALS[lower];

  if (irregular) {
    return applyCase({ source: word, target: irregular });
  }

  if (lower.endsWith('sis')) {
    return applyCase({ source: word, target: `${lower.slice(0, -2)}es` });
  }

  if (/(?:s|sh|ch|x|z)$/.test(lower)) {
    return `${word}es`;
  }

  if (/[^aeiou]y$/i.test(word)) {
    return `${word.slice(0, -1)}ies`;
  }

  return `${word}s`;
}

function inflectLastWord({
  nounPhrase,
  count,
}: {
  nounPhrase: string;
  count: number;
}): string {
  const words = nounPhrase.split(/\s+/).filter(Boolean);
  const lastWord = words.at(-1);

  if (!lastWord) {
    return nounPhrase;
  }

  const singular = toSingular(lastWord);
  const inflected = count === 1 ? singular : toPlural(singular);

  return [...words.slice(0, -1), inflected].join(' ');
}

export function formatCountedToolCallLabel({
  label,
  count,
}: CountedToolCallLabelInput): string {
  const trimmed = label.trim();

  if (!trimmed || !Number.isInteger(count) || count <= 0) {
    return label;
  }

  const spaceIndex = trimmed.indexOf(' ');

  if (spaceIndex === -1) {
    return label;
  }

  const verb = trimmed.slice(0, spaceIndex);
  const nounPhrase = trimmed.slice(spaceIndex + 1).trim();

  if (!nounPhrase) {
    return label;
  }

  return `${verb} ${count} ${inflectLastWord({ nounPhrase, count })}`;
}
